import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { PitchSlide } from '../../data/slides';
import { appTheme, pitchTheme } from '../../styles/theme';

const LIST_GAP = 8;

const Sidebar = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: ${appTheme.surface};
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusLg};
  overflow-y: auto;
  box-shadow: ${appTheme.shadowSm};
`;

const SlideList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${LIST_GAP}px;
`;

const rowBackground = ({ $active, $placeholder }: { $active: boolean; $placeholder: boolean }): string => {
  if ($placeholder) {
    return appTheme.mint;
  }
  return $active ? appTheme.mint : appTheme.surface;
};

const rowShadow = ({ $active, $placeholder }: { $active: boolean; $placeholder: boolean }): string => {
  if ($placeholder) {
    return `inset 0 0 0 1px ${appTheme.green}`;
  }
  return $active ? `inset 0 0 0 2px ${appTheme.green}` : `inset 0 0 0 1px ${appTheme.border}`;
};

const SlideRow = styled.div<{
  $active: boolean;
  $placeholder: boolean;
  $animate: boolean;
}>`
  display: flex;
  align-items: stretch;
  gap: 6px;
  border-radius: ${appTheme.radiusMd};
  border: 1px dashed
    ${({ $placeholder }) => ($placeholder ? appTheme.green : 'transparent')};
  background: ${rowBackground};
  box-shadow: ${rowShadow};
  opacity: ${({ $placeholder }) => ($placeholder ? 0.5 : 1)};
  transition: ${({ $animate }) =>
    $animate ? 'transform 0.16s ease' : 'background 0.12s ease, box-shadow 0.12s ease'};
  will-change: transform;

  &:hover {
    box-shadow: ${({ $placeholder }) =>
      $placeholder
        ? `inset 0 0 0 1px ${appTheme.green}`
        : `inset 0 0 0 1px ${appTheme.green}`};
  }
`;

const SlideRowInner = styled.div<{ $hidden?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

const DragOverlayCard = styled.div`
  display: flex;
  align-items: stretch;
  gap: 6px;
  border-radius: ${appTheme.radiusMd};
  border: 1px solid ${appTheme.green};
  background: ${appTheme.surface};
  box-shadow: ${appTheme.shadowLg};
  cursor: grabbing;
  pointer-events: none;
`;

const DragHandle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0 4px 0 8px;
  color: ${appTheme.textMuted};
  cursor: grab;
  flex-shrink: 0;
  touch-action: none;

  &:hover {
    color: ${appTheme.green};
  }

  &:active {
    cursor: grabbing;
  }
`;

const GripColumn = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GripDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
`;

const SlideButton = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px 10px 0;
  border: none;
  background: transparent;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  min-width: 0;
`;

const SlideLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${appTheme.green};
`;

const SlideTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${appTheme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AddSlideButton = styled.button`
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: ${appTheme.radiusMd};
  border: 1px dashed ${appTheme.border};
  background: ${appTheme.pageBg};
  color: ${appTheme.green};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: ${appTheme.green};
    background: ${appTheme.mint};
  }
`;

const DragGrip: React.FC = () => (
  <>
    <GripColumn aria-hidden>
      <GripDot />
      <GripDot />
      <GripDot />
    </GripColumn>
    <GripColumn aria-hidden>
      <GripDot />
      <GripDot />
      <GripDot />
    </GripColumn>
  </>
);

interface DragState {
  fromIndex: number;
  hoverIndex: number;
  pointerY: number;
  offsetY: number;
  width: number;
  left: number;
  /** Row tops/heights measured once at drag start, in original list order. */
  tops: number[];
  heights: number[];
}

/**
 * Destination index computed from static midpoints captured at drag start.
 * Using fixed measurements (not live rects of transformed rows) keeps the
 * result monotonic in pointer Y, which prevents oscillation/flicker.
 */
const hoverIndexFromPointer = (pointerY: number, drag: DragState): number => {
  let passed = 0;
  for (let i = 0; i < drag.tops.length; i += 1) {
    if (i === drag.fromIndex) {
      continue;
    }
    if (pointerY > drag.tops[i] + drag.heights[i] / 2) {
      passed += 1;
    }
  }
  return passed;
};

/** Translate offset for each row while dragging, keeping DOM order stable. */
const rowOffset = (index: number, drag: DragState): number => {
  const { fromIndex, hoverIndex, heights } = drag;
  const shift = heights[fromIndex] + LIST_GAP;

  if (index === fromIndex) {
    if (hoverIndex > fromIndex) {
      let sum = 0;
      for (let i = fromIndex + 1; i <= hoverIndex; i += 1) {
        sum += heights[i] + LIST_GAP;
      }
      return sum;
    }
    if (hoverIndex < fromIndex) {
      let sum = 0;
      for (let i = hoverIndex; i < fromIndex; i += 1) {
        sum += heights[i] + LIST_GAP;
      }
      return -sum;
    }
    return 0;
  }

  if (fromIndex < hoverIndex && index > fromIndex && index <= hoverIndex) {
    return -shift;
  }
  if (fromIndex > hoverIndex && index >= hoverIndex && index < fromIndex) {
    return shift;
  }
  return 0;
};

interface SlideSidebarProps {
  slides: PitchSlide[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onAddSlide: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

const SlideSidebar: React.FC<SlideSidebarProps> = ({
  slides,
  selectedIndex,
  onSelect,
  onAddSlide,
  onReorder,
}) => {
  const { t } = useTranslation();
  const [drag, setDrag] = useState<DragState | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;

  const startDrag = (event: React.PointerEvent, index: number) => {
    if (event.button !== 0) {
      return;
    }

    const tops: number[] = [];
    const heights: number[] = [];
    for (const slide of slides) {
      const row = rowRefs.current.get(slide.id);
      if (!row) {
        return;
      }
      const rect = row.getBoundingClientRect();
      tops.push(rect.top);
      heights.push(rect.height);
    }

    event.preventDefault();
    event.stopPropagation();
    document.body.style.userSelect = 'none';

    setDrag({
      fromIndex: index,
      hoverIndex: index,
      pointerY: event.clientY,
      offsetY: event.clientY - tops[index],
      width: rowRefs.current.get(slides[index].id)?.getBoundingClientRect().width ?? 0,
      left: rowRefs.current.get(slides[index].id)?.getBoundingClientRect().left ?? 0,
      tops,
      heights,
    });

    const handlePointerMove = (moveEvent: PointerEvent) => {
      setDrag((current) => {
        if (!current) {
          return null;
        }
        return {
          ...current,
          pointerY: moveEvent.clientY,
          hoverIndex: hoverIndexFromPointer(moveEvent.clientY, current),
        };
      });
    };

    const handlePointerEnd = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
      document.body.style.userSelect = '';
      setDrag((current) => {
        if (current && current.fromIndex !== current.hoverIndex) {
          onReorderRef.current(current.fromIndex, current.hoverIndex);
        }
        return null;
      });
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
  };

  const renderSlideContent = (slide: PitchSlide, index: number, enableDrag: boolean) => (
    <>
      <DragHandle
        onPointerDown={enableDrag ? (event) => startDrag(event, index) : undefined}
        aria-label={enableDrag ? t('editor.slides.dragSlide', { index: index + 1 }) : undefined}
        title={enableDrag ? t('editor.slides.dragToReorder') : undefined}
      >
        <DragGrip />
      </DragHandle>
      <SlideButton
        type="button"
        onClick={() => onSelect(index)}
        tabIndex={enableDrag ? 0 : -1}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <SlideLabel>
          {index + 1} · {slide.tabLabel}
        </SlideLabel>
        <SlideTitle>{slide.title}</SlideTitle>
      </SlideButton>
    </>
  );

  const draggedSlide = drag ? slides[drag.fromIndex] : null;

  return (
    <Sidebar aria-label={t('editor.slides.ariaLabel')}>
      <SlideList>
        {slides.map((slide, index) => {
          const isDragging = drag?.fromIndex === index;
          const offset = drag ? rowOffset(index, drag) : 0;

          return (
            <SlideRow
              key={slide.id}
              ref={(element) => {
                if (element) {
                  rowRefs.current.set(slide.id, element);
                } else {
                  rowRefs.current.delete(slide.id);
                }
              }}
              $active={index === selectedIndex && !isDragging}
              $placeholder={Boolean(isDragging)}
              $animate={Boolean(drag)}
              style={{ transform: offset ? `translateY(${offset}px)` : undefined }}
            >
              <SlideRowInner $hidden={isDragging} aria-hidden={isDragging}>
                {renderSlideContent(slide, index, !isDragging)}
              </SlideRowInner>
            </SlideRow>
          );
        })}
      </SlideList>

      {drag &&
        draggedSlide &&
        createPortal(
          <DragOverlayCard
            style={{
              top: drag.pointerY - drag.offsetY,
              left: drag.left,
              width: drag.width,
              position: 'fixed',
              zIndex: 1000,
            }}
          >
            {renderSlideContent(draggedSlide, drag.fromIndex, false)}
          </DragOverlayCard>,
          document.body,
        )}

      <AddSlideButton type="button" onClick={onAddSlide}>
        {t('editor.slides.addSlide')}
      </AddSlideButton>
    </Sidebar>
  );
};

export default SlideSidebar;
